import React, { memo, useDeferredValue, useMemo, useState } from "react";
import {
  QEEG_BAND_RANGES,
  QEEG_BIOMARKER_INTENDED_USE,
  QEEG_BIOMARKER_LABELS,
  QEEG_POWER_RATIO_LABELS,
  QEEG_TREND_LABELS,
  QEEG_TREND_TYPES,
  buildQEEGTrendProvenance,
  computeAEEGTrend,
  computeAmplitudeTrend,
  computeAsymmetryTrend,
  computeBandpowerTrend,
  computeBiomarkerSummary,
  computeBiomarkerTrend,
  computePowerRatioTrend,
  computeRhythmicityProxy,
  computeSpectralEdgeTrend,
  computeSuppressionRatio,
  computeDsaMatrixFromSignal,
  downsampleSpectrogramMatrix,
  downsampleTrendSamples,
  epochPowers,
  filterTrendSamplesToWindow,
  formatBiomarkerReportFields,
  mapCursorToTrendIndex,
  mapDsaColumnToTimeSeconds,
  resolveTrendWindow,
  type QEEGBandName,
  type QEEGBiomarkerKind,
  type QEEGPowerRatioKind,
  type QEEGTrendProvenance,
  type QEEGTrendSample,
  type QEEGTrendType,
} from "../utils/qeegTrendTransforms";
import type { QeegLongStudyClientSeries } from "../utils/qeegLongStudyTrendClient";
import {
  createDefaultQeegRawLayout,
  mapTrendClickToRawSeek,
} from "../utils/qeegRawLinkedLayout";
import {
  QEEG_IFU_UI_BADGE,
  QEEG_IFU_UI_SUMMARY,
  NORMATIVE_ZMAP_IFU_UI_BADGE,
  VSBASELINE_IFU_UI_BADGE,
} from "../utils/assistiveIfuFreeze";
import {
  computeVsBaselineSeries,
  lookupNormativeStat,
  computeZScore,
} from "../utils/normativeZMapAndBaseline";
import { EegDockPanelLabeling } from "./EegDockPanelLabeling";
import "./QEEGTrendLane.css";

export const QEEG_INTENDED_USE_SUMMARY =
  "qEEG trends are non-diagnostic decision-support visualization aids for navigating raw EEG. They do not provide standalone clinical interpretation.";

export const QEEG_EXCLUDED_USE_SUMMARY =
  "Excluded use: autonomous diagnosis, closed-loop therapy control, and unattended alarming without clinician waveform review.";

const CLINICAL_BANDS: QEEGBandName[] = [
  "Delta",
  "Theta",
  "Alpha",
  "Beta",
  "Gamma",
];

const LANE_COLORS: Record<QEEGTrendType, string> = {
  spectrogram: "#5b8def",
  aeeg: "#3aa87a",
  amplitude: "#5b8def",
  asymmetry: "#c27a3a",
  rhythmicity: "#3aa87a",
  suppression: "#9b5de5",
  spectral_edge: "#e0a106",
  bandpower: "#4ecdc4",
  ratio: "#ff6b6b",
  seizure_burden: "#d64545",
  vs_baseline: "#2a9d8f",
  zscore_bandpower: "#264653",
};

const DEFAULT_VISIBLE: QEEGTrendType[] = [
  "spectrogram",
  "aeeg",
  "spectral_edge",
  "bandpower",
  "ratio",
  "asymmetry",
  "rhythmicity",
  "seizure_burden",
  "vs_baseline",
  "zscore_bandpower",
];

export type QEEGTrendLaneProps = {
  channelData?: Float32Array | number[];
  leftChannelData?: Float32Array | number[];
  rightChannelData?: Float32Array | number[];
  sampleRate: number;
  currentTimeSeconds: number;
  recordingDurationSeconds: number;
  spectrogramMatrix?: number[][];
  visible?: boolean;
  onToggleVisible?: (visible: boolean) => void;
  onSeek?: (timeSeconds: number) => void;
  halfWindowSeconds?: number;
  channelNames?: string[];
  montageName?: string | null;
  highPassHz?: number | null;
  lowPassHz?: number | null;
  notch50Hz?: boolean;
  notch60Hz?: boolean;
  artifactExclusions?: string[];
  provenanceOverride?: QEEGTrendProvenance | null;
  maxDisplayPoints?: number;
  /** Prefer materialized long-study series when ready (S1). */
  longStudySeries?: QeegLongStudyClientSeries[] | null;
  longStudyStatus?:
    | "idle"
    | "loading"
    | "ready"
    | "partial"
    | "unavailable"
    | "generating"
    | "failed";
  /** Assistive seizure-burden samples from Timescale/monitoring (S2). */
  seizureBurdenSamples?: QEEGTrendSample[] | null;
  /** CX-G09: enable change-from-baseline lane. */
  vsBaselineEnabled?: boolean;
  baselineFromSeconds?: number;
  baselineToSeconds?: number;
  /** Override initial lane visibility (ICU workbench / compact embeds). */
  initialVisibleLanes?: QEEGTrendType[];
};

const TrendStrip = memo(function TrendStrip({
  label,
  samples,
  currentTimeSeconds,
  color,
  onSeek,
  valueFormatter,
}: {
  label: string;
  samples: QEEGTrendSample[];
  currentTimeSeconds: number;
  color: string;
  onSeek?: (timeSeconds: number) => void;
  valueFormatter?: (value: number, secondary?: number) => string;
}) {
  const cursorIndex = mapCursorToTrendIndex(currentTimeSeconds, samples);
  const max = Math.max(
    1e-6,
    ...samples.map((s) => Math.max(Math.abs(s.value), Math.abs(s.secondaryValue || 0))),
  );

  return (
    <div className="qeeg-trend-strip" data-testid={`qeeg-strip-${label}`}>
      <div className="qeeg-trend-strip__label">{label}</div>
      <div
        className="qeeg-trend-strip__canvas"
        role="listbox"
        aria-label={`${label} trend lane. Select a point to jump to raw EEG.`}
      >
        {samples.map((sample, index) => {
          const height = Math.max(4, Math.round((Math.abs(sample.value) / max) * 100));
          const formatted = valueFormatter
            ? valueFormatter(sample.value, sample.secondaryValue)
            : sample.value.toFixed(3);
          return (
            <button
              key={`${sample.timeSeconds}-${index}`}
              type="button"
              role="option"
              aria-selected={index === cursorIndex}
              className={`qeeg-trend-strip__bar${index === cursorIndex ? " is-cursor" : ""}`}
              style={{ height: `${height}%`, background: color }}
              title={`${sample.timeSeconds.toFixed(1)}s: ${formatted}`}
              aria-label={`Jump to raw EEG at ${sample.timeSeconds.toFixed(1)} seconds, ${label} ${formatted}`}
              onClick={() => {
                if (!onSeek) return;
                const mapped = mapTrendClickToRawSeek(
                  createDefaultQeegRawLayout({ linkedSeekEnabled: true }),
                  sample.timeSeconds,
                );
                const seekSeconds = mapped?.seekSeconds ?? sample.timeSeconds;
                if (typeof performance !== "undefined") {
                  (window as any).__eegTrendSeekPerf = {
                    requestedAtMs: performance.now(),
                    seekSeconds,
                  };
                }
                onSeek(seekSeconds);
              }}
            />
          );
        })}
        {!samples.length && (
          <div className="qeeg-trend-strip__empty">No samples</div>
        )}
      </div>
    </div>
  );
});

function ProvenancePanel({ provenance }: { provenance: QEEGTrendProvenance }) {
  const filters = [
    provenance.filters.highPassHz != null
      ? `HP ${provenance.filters.highPassHz} Hz`
      : null,
    provenance.filters.lowPassHz != null
      ? `LP ${provenance.filters.lowPassHz} Hz`
      : null,
    provenance.filters.notch50Hz ? "Notch 50" : null,
    provenance.filters.notch60Hz ? "Notch 60" : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <details className="qeeg-trend-lane__provenance" data-testid="qeeg-trend-provenance">
      <summary>Trend generation provenance</summary>
      <dl>
        <div>
          <dt>Algorithm</dt>
          <dd>{provenance.algorithmVersion}</dd>
        </div>
        <div>
          <dt>Channel set</dt>
          <dd>{provenance.channelSet.length ? provenance.channelSet.join(", ") : "n/a"}</dd>
        </div>
        <div>
          <dt>Montage</dt>
          <dd>{provenance.montage || "n/a"}</dd>
        </div>
        <div>
          <dt>Sampling rate</dt>
          <dd>{provenance.samplingRateHz} Hz</dd>
        </div>
        <div>
          <dt>Filters</dt>
          <dd>{filters || "none recorded"}</dd>
        </div>
        <div>
          <dt>Artifact exclusions</dt>
          <dd>
            {provenance.artifactExclusions.length
              ? provenance.artifactExclusions.join(", ")
              : "none"}
          </dd>
        </div>
        <div>
          <dt>Epoch</dt>
          <dd>{provenance.epochSeconds}s</dd>
        </div>
        <div>
          <dt>Generated</dt>
          <dd>{provenance.generatedAtIso}</dd>
        </div>
      </dl>
    </details>
  );
}

export default function QEEGTrendLane({
  channelData,
  leftChannelData,
  rightChannelData,
  sampleRate,
  currentTimeSeconds,
  recordingDurationSeconds,
  spectrogramMatrix,
  visible = true,
  onToggleVisible,
  onSeek,
  halfWindowSeconds = 30,
  channelNames = [],
  montageName = null,
  highPassHz = null,
  lowPassHz = null,
  notch50Hz = false,
  notch60Hz = false,
  artifactExclusions = [],
  provenanceOverride = null,
  maxDisplayPoints = 180,
  longStudySeries = null,
  longStudyStatus = "idle",
  seizureBurdenSamples = null,
  vsBaselineEnabled = false,
  baselineFromSeconds = 0,
  baselineToSeconds = 300,
  initialVisibleLanes,
}: QEEGTrendLaneProps) {
  const [windowHalf, setWindowHalf] = useState(halfWindowSeconds);
  const [visibleLanes, setVisibleLanes] = useState<QEEGTrendType[]>(
    initialVisibleLanes?.length ? initialVisibleLanes : DEFAULT_VISIBLE,
  );
  const [bandName, setBandName] = useState<QEEGBandName>("Alpha");
  const [ratioKind, setRatioKind] = useState<QEEGPowerRatioKind>("alpha_delta");
  const [biomarkerKind, setBiomarkerKind] =
    useState<QEEGBiomarkerKind>("dar");
  const [focusType, setFocusType] = useState<QEEGTrendType | "all">("all");
  const deferredCurrentTimeSeconds = useDeferredValue(currentTimeSeconds);

  const window = useMemo(
    () =>
      resolveTrendWindow(
        deferredCurrentTimeSeconds,
        windowHalf,
        recordingDurationSeconds,
      ),
    [deferredCurrentTimeSeconds, windowHalf, recordingDurationSeconds],
  );

  const provenance = useMemo(
    () =>
      provenanceOverride ||
      buildQEEGTrendProvenance({
        channelSet: channelNames,
        montage: montageName,
        samplingRateHz: sampleRate,
        filters: {
          highPassHz,
          lowPassHz,
          notch50Hz,
          notch60Hz,
        },
        artifactExclusions,
        epochSeconds: 2,
      }),
    [
      provenanceOverride,
      channelNames,
      montageName,
      sampleRate,
      highPassHz,
      lowPassHz,
      notch50Hz,
      notch60Hz,
      artifactExclusions,
    ],
  );

  const longStudyByType = useMemo(() => {
    const map = new Map<string, QeegLongStudyClientSeries>();
    for (const series of longStudySeries || []) {
      map.set(series.type, series);
    }
    return map;
  }, [longStudySeries]);

  const preferLongStudy =
    longStudyStatus === "ready" || longStudyStatus === "partial";

  const amplitude = useMemo(() => {
    if (preferLongStudy && longStudyByType.get("amplitude")?.samples.length) {
      return longStudyByType.get("amplitude")!.samples;
    }
    return channelData?.length
      ? computeAmplitudeTrend(channelData, sampleRate, 1)
      : [];
  }, [preferLongStudy, longStudyByType, channelData, sampleRate]);
  const aeeg = useMemo(() => {
    if (preferLongStudy && longStudyByType.get("aeeg")?.samples.length) {
      return longStudyByType.get("aeeg")!.samples;
    }
    return channelData?.length
      ? computeAEEGTrend(channelData, sampleRate, 2)
      : [];
  }, [preferLongStudy, longStudyByType, channelData, sampleRate]);
  const asymmetry = useMemo(() => {
    if (preferLongStudy && longStudyByType.get("asymmetry")?.samples.length) {
      return longStudyByType.get("asymmetry")!.samples;
    }
    return computeAsymmetryTrend(
      epochPowers(leftChannelData, sampleRate),
      epochPowers(rightChannelData, sampleRate),
      1,
    );
  }, [
    preferLongStudy,
    longStudyByType,
    leftChannelData,
    rightChannelData,
    sampleRate,
  ]);
  const rhythmicity = useMemo(() => {
    if (preferLongStudy && longStudyByType.get("rhythmicity")?.samples.length) {
      return longStudyByType.get("rhythmicity")!.samples;
    }
    return channelData?.length
      ? computeRhythmicityProxy(channelData, sampleRate, 2)
      : [];
  }, [preferLongStudy, longStudyByType, channelData, sampleRate]);
  const suppression = useMemo(() => {
    if (preferLongStudy && longStudyByType.get("suppression")?.samples.length) {
      return longStudyByType.get("suppression")!.samples;
    }
    return channelData?.length
      ? computeSuppressionRatio(channelData, sampleRate, 5, 1)
      : [];
  }, [preferLongStudy, longStudyByType, channelData, sampleRate]);
  const spectralEdge = useMemo(() => {
    if (
      preferLongStudy &&
      longStudyByType.get("spectral_edge")?.samples.length
    ) {
      return longStudyByType.get("spectral_edge")!.samples;
    }
    return channelData?.length
      ? computeSpectralEdgeTrend(channelData, sampleRate, 2, 95)
      : [];
  }, [preferLongStudy, longStudyByType, channelData, sampleRate]);
  const bandpower = useMemo(() => {
    if (preferLongStudy && longStudyByType.get("bandpower")?.samples.length) {
      return longStudyByType.get("bandpower")!.samples;
    }
    return channelData?.length
      ? computeBandpowerTrend(channelData, sampleRate, bandName, 2)
      : [];
  }, [preferLongStudy, longStudyByType, channelData, sampleRate, bandName]);
  const ratio = useMemo(() => {
    if (preferLongStudy && longStudyByType.get("ratio")?.samples.length) {
      return longStudyByType.get("ratio")!.samples;
    }
    return channelData?.length
      ? computePowerRatioTrend(channelData, sampleRate, ratioKind, 2)
      : [];
  }, [preferLongStudy, longStudyByType, channelData, sampleRate, ratioKind]);
  const biomarker = useMemo(
    () =>
      channelData?.length
        ? computeBiomarkerTrend(channelData, sampleRate, biomarkerKind, 2)
        : [],
    [channelData, sampleRate, biomarkerKind],
  );
  const biomarkerSummaries = useMemo(() => {
    if (!channelData?.length) return [];
    return (["dar", "tbr", "str"] as QEEGBiomarkerKind[]).map((kind) =>
      computeBiomarkerSummary(channelData, sampleRate, kind, {
        montage: montageName,
        channelSet: channelNames,
        filters: { highPassHz, lowPassHz, notch50Hz, notch60Hz },
      }),
    );
  }, [
    channelData,
    sampleRate,
    montageName,
    channelNames,
    highPassHz,
    lowPassHz,
    notch50Hz,
    notch60Hz,
  ]);
  const biomarkerReportFields = useMemo(
    () => formatBiomarkerReportFields(biomarkerSummaries),
    [biomarkerSummaries],
  );
  const dsa = useMemo(() => {
    if (preferLongStudy) {
      const longDsa =
        longStudyByType.get("dsa")?.matrix ||
        longStudyByType.get("spectrogram")?.matrix;
      if (longDsa?.length) {
        return downsampleSpectrogramMatrix(longDsa, 120);
      }
    }
    if (spectrogramMatrix?.length) {
      return downsampleSpectrogramMatrix(spectrogramMatrix, 120);
    }
    if (channelData?.length) {
      return computeDsaMatrixFromSignal(channelData, sampleRate, 2, 120);
    }
    return [];
  }, [
    preferLongStudy,
    longStudyByType,
    spectrogramMatrix,
    channelData,
    sampleRate,
  ]);
  const dsaRows = useMemo(
    () =>
      dsa.slice(0, 24).map((row) => ({
        row,
        max: Math.max(...row, 1e-6),
      })),
    [dsa],
  );

  const laneSamples: Record<
    Exclude<QEEGTrendType, "spectrogram">,
    QEEGTrendSample[]
  > = useMemo(() => {
    const prepare = (samples: QEEGTrendSample[]) =>
      downsampleTrendSamples(
        filterTrendSamplesToWindow(samples, window),
        maxDisplayPoints,
      );
    return {
      aeeg: prepare(aeeg),
      amplitude: prepare(amplitude),
      asymmetry: prepare(asymmetry),
      rhythmicity: prepare(rhythmicity),
      suppression: prepare(suppression),
      spectral_edge: prepare(spectralEdge),
      bandpower: prepare(bandpower),
      ratio: prepare(ratio),
      seizure_burden: prepare(seizureBurdenSamples || []),
      vs_baseline: prepare(
        vsBaselineEnabled
          ? computeVsBaselineSeries(
              bandpower,
              baselineFromSeconds,
              baselineToSeconds,
            ).samples
          : [],
      ),
      zscore_bandpower: prepare(
        bandpower.map((sample) => {
          const bandKey = String(bandName).toLowerCase();
          const stat = lookupNormativeStat("*", bandKey);
          return {
            timeSeconds: sample.timeSeconds,
            value: computeZScore(sample.value, stat.mean, stat.std),
          };
        }),
      ),
    };
  }, [
    aeeg,
    amplitude,
    asymmetry,
    rhythmicity,
    suppression,
    spectralEdge,
    bandpower,
    ratio,
    seizureBurdenSamples,
    vsBaselineEnabled,
    baselineFromSeconds,
    baselineToSeconds,
    bandName,
    window,
    maxDisplayPoints,
  ]);

  const shownTypes = QEEG_TREND_TYPES.filter((type) => {
    if (focusType !== "all" && type !== focusType) return false;
    return visibleLanes.includes(type);
  });

  const toggleLane = (type: QEEGTrendType) => {
    setVisibleLanes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  if (!visible) {
    return (
      <div className="qeeg-trend-lane qeeg-trend-lane--collapsed">
        <button
          type="button"
          className="qeeg-trend-lane__toggle"
          onClick={() => onToggleVisible?.(true)}
        >
          Show qEEG trends
        </button>
      </div>
    );
  }

  return (
    <div className="qeeg-trend-lane" data-testid="qeeg-trend-lane">
      <EegDockPanelLabeling
        className="qeeg-trend-lane__intended-use"
        ariaLabel="qEEG intended use"
        data-testid="qeeg-intended-use"
        badge={QEEG_IFU_UI_BADGE}
        summary={QEEG_IFU_UI_SUMMARY}
        intendedUse={QEEG_INTENDED_USE_SUMMARY}
        excludedUse={QEEG_EXCLUDED_USE_SUMMARY}
        sessionNotes={
          <>
            <div data-testid="qeeg-biomarker-research-label">
              <strong>Biomarkers (DAR/TBR/STR):</strong>{" "}
              {QEEG_BIOMARKER_INTENDED_USE.label}
            </div>
            {longStudyStatus !== "idle" ? (
              <div data-testid="qeeg-long-study-status">
                <strong>Long-study:</strong> {longStudyStatus}
                {preferLongStudy
                  ? " — using validated API series"
                  : " — window-local preview until materialization is ready"}
              </div>
            ) : null}
          </>
        }
      />

      <div className="eeg-dock-panel-chrome__toolbar qeeg-trend-lane__toolbar">
        <div className="qeeg-trend-lane__tabs" role="tablist" aria-label="qEEG trend focus">
          <button
            type="button"
            role="tab"
            aria-selected={focusType === "all"}
            className={`qeeg-trend-lane__tab${focusType === "all" ? " is-active" : ""}`}
            onClick={() => setFocusType("all")}
          >
            All lanes
          </button>
          {QEEG_TREND_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              role="tab"
              aria-selected={focusType === type}
              className={`qeeg-trend-lane__tab${focusType === type ? " is-active" : ""}`}
              onClick={() => setFocusType(type)}
            >
              {QEEG_TREND_LABELS[type]}
            </button>
          ))}
        </div>
        <label className="qeeg-trend-lane__window">
          Window ±
          <select
            value={windowHalf}
            onChange={(event) => setWindowHalf(Number(event.target.value))}
            aria-label="Trend time window"
          >
            <option value={15}>15s</option>
            <option value={30}>30s</option>
            <option value={60}>60s</option>
            <option value={120}>120s</option>
            <option value={300}>300s</option>
          </select>
        </label>
        <label className="qeeg-trend-lane__window">
          Band
          <select
            value={bandName}
            onChange={(event) => setBandName(event.target.value as QEEGBandName)}
            aria-label="Bandpower frequency band"
          >
            {CLINICAL_BANDS.map((band) => (
              <option key={band} value={band}>
                {band} ({QEEG_BAND_RANGES[band][0]}–{QEEG_BAND_RANGES[band][1]} Hz)
              </option>
            ))}
          </select>
        </label>
        <label className="qeeg-trend-lane__window">
          Ratio
          <select
            value={ratioKind}
            onChange={(event) =>
              setRatioKind(event.target.value as QEEGPowerRatioKind)
            }
            aria-label="Power ratio kind"
          >
            {(Object.keys(QEEG_POWER_RATIO_LABELS) as QEEGPowerRatioKind[]).map(
              (kind) => (
                <option key={kind} value={kind}>
                  {QEEG_POWER_RATIO_LABELS[kind]}
                </option>
              ),
            )}
          </select>
        </label>
        <label className="qeeg-trend-lane__window">
          Biomarker
          <select
            value={biomarkerKind}
            onChange={(event) =>
              setBiomarkerKind(event.target.value as QEEGBiomarkerKind)
            }
            aria-label="Advanced biomarker kind"
            data-testid="qeeg-biomarker-kind"
          >
            {(Object.keys(QEEG_BIOMARKER_LABELS) as QEEGBiomarkerKind[]).map(
              (kind) => (
                <option key={kind} value={kind}>
                  {QEEG_BIOMARKER_LABELS[kind]}
                </option>
              ),
            )}
          </select>
        </label>
        <button
          type="button"
          className="qeeg-trend-lane__toggle"
          onClick={() => onToggleVisible?.(false)}
        >
          Hide
        </button>
      </div>

      <div className="qeeg-trend-lane__lane-toggles" aria-label="Toggle trend lanes">
        {QEEG_TREND_TYPES.map((type) => (
          <label key={type} className="qeeg-trend-lane__lane-toggle">
            <input
              type="checkbox"
              checked={visibleLanes.includes(type)}
              onChange={() => toggleLane(type)}
              aria-label={`Show ${QEEG_TREND_LABELS[type]} lane`}
            />
            {QEEG_TREND_LABELS[type]}
          </label>
        ))}
      </div>

      <div className="qeeg-trend-lane__meta">
        Sync window {window.startSeconds.toFixed(1)}s – {window.endSeconds.toFixed(1)}s
        {" · "}
        Cursor {currentTimeSeconds.toFixed(2)}s
        {" · "}
        Click any trend point to jump to raw EEG
      </div>

      <ProvenancePanel provenance={provenance} />

      <div className="qeeg-trend-lane__lanes" data-testid="qeeg-trend-lanes">
        {shownTypes.includes("spectrogram") && (
          <div className="qeeg-trend-lane__dsa" aria-label="FFT spectrogram DSA">
            <div className="qeeg-trend-strip__label">FFT / DSA</div>
            {dsaRows.length ? (
              dsaRows.map(({ row, max }, rowIndex) => (
                <div key={`dsa-row-${rowIndex}`} className="qeeg-trend-lane__dsa-row">
                  {row.map((value, colIndex) => (
                    <button
                      key={`dsa-${rowIndex}-${colIndex}`}
                      type="button"
                      className="qeeg-trend-lane__dsa-cell"
                      style={{
                        opacity: Math.max(0.08, Math.min(1, value / max)),
                      }}
                      aria-label={`Jump to raw EEG from DSA column ${colIndex + 1}`}
                      title={`DSA t=${mapDsaColumnToTimeSeconds(colIndex, row.length, window).toFixed(1)}s`}
                      onClick={() =>
                        onSeek?.(
                          mapDsaColumnToTimeSeconds(colIndex, row.length, window),
                        )
                      }
                    />
                  ))}
                </div>
              ))
            ) : (
              <div className="qeeg-trend-strip__empty">
                Spectrogram matrix unavailable — other trend lanes still sync to playhead.
              </div>
            )}
          </div>
        )}

        {shownTypes.includes("aeeg") && (
          <TrendStrip
            label="aEEG"
            samples={laneSamples.aeeg}
            currentTimeSeconds={currentTimeSeconds}
            color={LANE_COLORS.aeeg}
            onSeek={onSeek}
            valueFormatter={(upper, lower) =>
              `U ${upper.toFixed(2)} / L ${(lower ?? 0).toFixed(2)}`
            }
          />
        )}
        {shownTypes.includes("amplitude") && (
          <TrendStrip
            label="Amplitude"
            samples={laneSamples.amplitude}
            currentTimeSeconds={currentTimeSeconds}
            color={LANE_COLORS.amplitude}
            onSeek={onSeek}
          />
        )}
        {shownTypes.includes("asymmetry") && (
          <TrendStrip
            label="Asymmetry"
            samples={laneSamples.asymmetry}
            currentTimeSeconds={currentTimeSeconds}
            color={LANE_COLORS.asymmetry}
            onSeek={onSeek}
          />
        )}
        {shownTypes.includes("rhythmicity") && (
          <TrendStrip
            label="Rhythmicity"
            samples={laneSamples.rhythmicity}
            currentTimeSeconds={currentTimeSeconds}
            color={LANE_COLORS.rhythmicity}
            onSeek={onSeek}
          />
        )}
        {shownTypes.includes("suppression") && (
          <TrendStrip
            label="Suppression"
            samples={laneSamples.suppression}
            currentTimeSeconds={currentTimeSeconds}
            color={LANE_COLORS.suppression}
            onSeek={onSeek}
          />
        )}
        {shownTypes.includes("spectral_edge") && (
          <TrendStrip
            label="Spectral edge"
            samples={laneSamples.spectral_edge}
            currentTimeSeconds={currentTimeSeconds}
            color={LANE_COLORS.spectral_edge}
            onSeek={onSeek}
            valueFormatter={(hz) => `${hz.toFixed(1)} Hz`}
          />
        )}
        {shownTypes.includes("bandpower") && (
          <TrendStrip
            label={`Bandpower (${bandName})`}
            samples={laneSamples.bandpower}
            currentTimeSeconds={currentTimeSeconds}
            color={LANE_COLORS.bandpower}
            onSeek={onSeek}
          />
        )}
        {shownTypes.includes("ratio") && (
          <TrendStrip
            label={`Ratio (${QEEG_POWER_RATIO_LABELS[ratioKind]})`}
            samples={laneSamples.ratio}
            currentTimeSeconds={currentTimeSeconds}
            color={LANE_COLORS.ratio}
            onSeek={onSeek}
          />
        )}
        {shownTypes.includes("seizure_burden") && (
          <TrendStrip
            label="Seizure burden"
            samples={laneSamples.seizure_burden}
            currentTimeSeconds={currentTimeSeconds}
            color={LANE_COLORS.seizure_burden}
            onSeek={onSeek}
            valueFormatter={(v) => `${(v * 100).toFixed(1)}%`}
          />
        )}
        {shownTypes.includes("vs_baseline") && (
          <TrendStrip
            label={`vsBaseline (${VSBASELINE_IFU_UI_BADGE})`}
            samples={laneSamples.vs_baseline}
            currentTimeSeconds={currentTimeSeconds}
            color={LANE_COLORS.vs_baseline}
            onSeek={onSeek}
            valueFormatter={(v) => `${(v * 100).toFixed(1)}% Δ`}
          />
        )}
        {shownTypes.includes("zscore_bandpower") && (
          <TrendStrip
            label={`Normative z (${NORMATIVE_ZMAP_IFU_UI_BADGE})`}
            samples={laneSamples.zscore_bandpower}
            currentTimeSeconds={currentTimeSeconds}
            color={LANE_COLORS.zscore_bandpower}
            onSeek={onSeek}
            valueFormatter={(v) => `${v.toFixed(2)} z`}
          />
        )}
        <TrendStrip
          label={`${QEEG_BIOMARKER_LABELS[biomarkerKind]} (research)`}
          samples={downsampleTrendSamples(
            filterTrendSamplesToWindow(biomarker, window),
            maxDisplayPoints,
          )}
          currentTimeSeconds={currentTimeSeconds}
          color="#7a5af8"
          onSeek={onSeek}
        />
        <div
          className="qeeg-trend-lane__meta"
          data-testid="qeeg-biomarker-report-fields"
        >
          Report fields:{" "}
          {biomarkerReportFields.fields
            .map((f) => `${f.label}=${f.value}`)
            .join(" · ")}
          {" · "}
          {biomarkerReportFields.disclaimer}
        </div>
      </div>
    </div>
  );
}
